import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Paper, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { CostingService } from '../../services/costing.service';
import { AuthService } from '../../services/auth.service';
import { OrderCostDetail as Detail } from '../../interfaces/costing.interface';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { useToast } from '../../context/ToastContext';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { toLocalIsoDate } from '../../utils/dateUtils';

const OrderCostDetail: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution()!;
  const canManage = usePermission(PERMISSIONS.COSTING_MANAGE);
  const [d, setD] = useState<Detail | null>(null);
  const [busy, setBusy] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferDate, setTransferDate] = useState('');

  const load = useCallback(async () => {
    try { setD(await CostingService.getOrder(institutionId, orderId!)); }
    catch (err) { toast.showApiError(err, 'Could not load the order cost sheet'); }
  }, [institutionId, orderId, toast]);

  useEffect(() => { load(); }, [load]);

  const act = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(true);
    try { await fn(); toast.showSuccess(label); await load(); }
    catch (err) { toast.showApiError(err, `${label} failed`); }
    finally { setBusy(false); }
  };

  if (!d) return <Box sx={{ p: 2 }}><Typography>Loading...</Typography></Box>;
  const r = d.row;
  const toTransfer = d.finishedQuantity - d.transferredQuantity;

  return (
    <Box sx={{ p: 2 }} data-help-page="costing.orderDetail.page">
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }} data-help-section="costing.orderDetail.actions">
        <Typography variant="h5" sx={{ flex: '1 1 240px' }}>Order {r.orderNumber} <Chip size="small" label={humanizeEnum(r.status)} /></Typography>
        <Button data-help="costing.orderDetail.allOrders" component={RouterLink} to="/costing/orders">All orders</Button>
        {canManage && (
          <>
            <Button data-help="costing.orderDetail.transfer" variant="outlined" disabled={busy || toTransfer <= 0}
              onClick={() => { setTransferDate(toLocalIsoDate()); setTransferOpen(true); }}>
              Transfer WIP to FG ({Math.max(toTransfer, 0)} pcs)
            </Button>
            <Button data-help="costing.orderDetail.close" variant="outlined" color="warning" disabled={busy}
              onClick={() => act('Order cost closed', () => CostingService.closeOrder(institutionId, r.orderId, {}))}>
              Close costing
            </Button>
          </>
        )}
      </Box>
      <Dialog data-help-page="costing.orderDetail.dlg" open={transferOpen} onClose={() => setTransferOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Transfer WIP to finished goods</DialogTitle>
        <DialogContent>
          <TextField data-help="costing.orderDetail.dlg.date" label="Transfer date" type="date" value={transferDate} onChange={e => setTransferDate(e.target.value)}
            fullWidth margin="dense" InputLabelProps={{ shrink: true }}
            inputProps={{ min: d.earliestEventDate ?? undefined, max: toLocalIsoDate() }}
            helperText={d.earliestEventDate ? `Not before ${d.earliestEventDate} (last costing event or absorbed month); not in the future.` : 'Not in the future.'} />
        </DialogContent>
        <DialogActions>
          <Button data-help="costing.orderDetail.dlg.cancel" onClick={() => setTransferOpen(false)}>Cancel</Button>
          <Button data-help="costing.orderDetail.dlg.confirm" variant="contained" disabled={busy || !transferDate}
            onClick={async () => {
              setTransferOpen(false);
              await act('WIP transferred to finished goods', () => CostingService.transferToFg(institutionId, r.orderId, { date: transferDate }));
            }}>Transfer</Button>
        </DialogActions>
      </Dialog>
      {d.notes.map(n => <Alert key={n} severity="warning" sx={{ mb: 1 }}>{n}</Alert>)}
      {r.closedWithResidual && <Alert severity="warning" sx={{ mb: 1 }}>Closed with a residual balance on WIP or FG; review the events below.</Alert>}

      <Paper sx={{ p: 2, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 3 }} data-testid="order-cost-summary" data-help-section="costing.orderDetail.summary">
        <Box><Typography variant="caption">Revenue (BDT)</Typography><Typography>{formatMoney(r.revenue)}</Typography></Box>
        <Box><Typography variant="caption">COGS</Typography><Typography>{formatMoney(r.cogs)}</Typography></Box>
        <Box><Typography variant="caption">Tagged expense</Typography><Typography>{formatMoney(r.taggedExpense)}</Typography></Box>
        <Box><Typography variant="caption">WIP</Typography><Typography>{formatMoney(r.wipBalance)}</Typography></Box>
        <Box><Typography variant="caption">FG</Typography><Typography>{formatMoney(r.fgBalance)}</Typography></Box>
        <Box><Typography variant="caption">Margin</Typography><Typography>{formatMoney(r.margin)}{r.costIncomplete ? ' (Cost incomplete)' : (r.marginPct != null ? ` (${r.marginPct}%)` : '')}</Typography></Box>
        {!!r.commission && <Box><Typography variant="caption">Buying-house commission</Typography><Typography>{formatMoney(r.commission)}</Typography></Box>}
      </Paper>

      <Paper sx={{ p: 2, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 3 }} data-help-section="costing.orderDetail.quantities">
        <Box><Typography variant="caption">Cut</Typography><Typography>{d.cutQuantity}</Typography></Box>
        <Box><Typography variant="caption">Sewn</Typography><Typography>{d.sewnQuantity}</Typography></Box>
        <Box><Typography variant="caption">Finished</Typography><Typography>{d.finishedQuantity}</Typography></Box>
        <Box><Typography variant="caption">Transferred to FG</Typography><Typography>{d.transferredQuantity}</Typography></Box>
        <Box><Typography variant="caption">Shipped (costed)</Typography><Typography>{d.shippedQuantity}</Typography></Box>
        <Box><Typography variant="caption">FG on hand</Typography><Typography>{d.fgQuantityOnHand}</Typography></Box>
      </Paper>

      {d.plan && (
        <Paper sx={{ p: 2, mb: 2 }} data-testid="order-cost-plan" data-help-section="costing.orderDetail.plan">
          <Typography variant="subtitle2">Plan (commercial report, {d.plan.costBasisLabel ?? 'cost basis'})</Typography>
          <Typography>Revenue {formatMoney(d.plan.revenue)} / cost {formatMoney(d.plan.costBasis)} / margin {formatMoney(d.plan.margin)}
            {d.plan.marginPct != null ? ` (${d.plan.marginPct}%)` : ''}</Typography>
        </Paper>
      )}

      <Typography variant="h6" sx={{ mb: 1 }}>Shipments</Typography>
      <TableContainer component={Paper} sx={{ mb: 3 }} data-help-section="costing.orderDetail.shipments">
        <Table size="small">
          <TableHead><TableRow><TableCell data-help="costing.orderDetail.shipments.col.invoice">Invoice</TableCell><TableCell data-help="costing.orderDetail.shipments.col.date">Date</TableCell><TableCell align="right" data-help="costing.orderDetail.shipments.col.amount">Amount</TableCell>
            <TableCell align="right" data-help="costing.orderDetail.shipments.col.rate">Rate</TableCell><TableCell align="right" data-help="costing.orderDetail.shipments.col.revenue">Revenue (BDT)</TableCell><TableCell data-help="costing.orderDetail.shipments.col.costed">Costed</TableCell></TableRow></TableHead>
          <TableBody>
            {d.shipments.map(s => (
              <TableRow key={s.exportInvoiceId}><TableCell>{s.invoiceNumber}</TableCell><TableCell>{s.invoiceDate}</TableCell>
                <TableCell align="right">{formatMoney(s.amount, s.currencyCode)}</TableCell><TableCell align="right">{s.rate ?? '-'}</TableCell>
                <TableCell align="right">{formatMoney(s.revenue)}</TableCell><TableCell>{s.costed ? 'Yes' : 'No'}</TableCell></TableRow>
            ))}
            {d.shipments.length === 0 && <TableRow><TableCell colSpan={6}>No export invoices for this order.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" sx={{ mb: 1 }}>Costing events</Typography>
      <TableContainer component={Paper} data-help-section="costing.orderDetail.events">
        <Table size="small" data-testid="order-cost-events">
          <TableHead><TableRow><TableCell data-help="costing.orderDetail.events.col.date">Date</TableCell><TableCell data-help="costing.orderDetail.events.col.event">Event</TableCell><TableCell align="right" data-help="costing.orderDetail.events.col.qty">Qty</TableCell>
            <TableCell align="right" data-help="costing.orderDetail.events.col.value">Value</TableCell><TableCell data-help="costing.orderDetail.events.col.status">Status</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>
            {d.events.map(e => (
              <TableRow key={e.id}><TableCell>{e.eventDate}</TableCell><TableCell>{humanizeEnum(e.kind)}</TableCell>
                <TableCell align="right">{e.quantity}</TableCell><TableCell align="right">{formatMoney(e.value)}</TableCell>
                <TableCell>{e.status}</TableCell>
                <TableCell align="right">{canManage && e.status === 'POSTED' && (
                  <Button data-help="costing.orderDetail.reverse" size="small" color="error" disabled={busy} onClick={() => act('Event reversed', () => CostingService.reverseEvent(institutionId, e.id))}>Reverse</Button>)}</TableCell></TableRow>
            ))}
            {d.events.length === 0 && <TableRow><TableCell colSpan={6}>No costing events yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default OrderCostDetail;
